import { CopyButton, None, formatRelative, formatTimestamp, formatUsd } from "@unizpay/ui";
import { ExternalLink as ExternalLinkIcon } from "lucide-react";
import type { ReactNode } from "react";
import { SOURCE_LABELS } from "../lib/paymentSource";
import type { PaymentSummary } from "../lib/types";
import { type Column, DataTable } from "./DataTable";
import { PaymentStatusPill } from "./StatusPill";

const amountColumn: Column<PaymentSummary> = {
  key: "amount",
  header: "Amount",
  align: "right",
  className: "whitespace-nowrap",
  cell: (p) => <span className="font-semibold tabular-nums">{formatUsd(p.amount, p.currency)}</span>,
};

const createdColumn: Column<PaymentSummary> = {
  key: "created",
  header: "Created",
  className: "whitespace-nowrap text-fg-muted",
  cell: (p) => (
    <time dateTime={p.created_at} title={formatRelative(p.created_at)}>
      {formatTimestamp(p.created_at)}
    </time>
  ),
};

/** What the payment is for, as the payer sees it on checkout; a dash when there is none. */
const paymentColumn: Column<PaymentSummary> = {
  key: "description",
  header: "Description",
  className: "max-w-64 truncate",
  cell: (p) =>
    p.description ? (
      <span title={`${p.description} (${p.payment_id})`} className="font-medium">
        {p.description}
      </span>
    ) : (
      <None />
    ),
};

const sourceColumn: Column<PaymentSummary> = {
  key: "source",
  header: "Source",
  className: "whitespace-nowrap text-fg-muted",
  cell: (p) => SOURCE_LABELS[p.source] ?? p.source,
};

/**
 * A link worth copying: paid on the hosted checkout (headless payments have none), and still
 * open to pay.
 */
const hasLink = (p: PaymentSummary) =>
  (p.source === "payment_link" || p.source === "api_checkout") && p.status === "awaiting_payment";

/** Copy / open the checkout link, and Deactivate payment links nobody has paid yet. */
const actionsColumn = (onDeactivate?: (p: PaymentSummary) => void): Column<PaymentSummary> => ({
  key: "actions",
  header: <span className="sr-only">Actions</span>,
  align: "right",
  cell: (p) =>
    hasLink(p) && (
      // Above the row link's overlay, so these stay clickable.
      <span className="relative z-10 inline-flex items-center gap-1">
        <CopyButton compact value={p.checkout_url} label="payment link" />
        <a
          href={p.checkout_url}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded p-1 text-fg-muted hover:bg-subtle hover:text-fg"
          aria-label="Open payment link (opens in a new tab)"
          title="Open payment link"
        >
          <ExternalLinkIcon aria-hidden="true" className="size-4" />
        </a>
        {onDeactivate && p.source === "payment_link" && (
          <button
            type="button"
            onClick={() => onDeactivate(p)}
            className="rounded px-1.5 py-1 text-xs font-medium text-danger hover:bg-danger-subtle"
          >
            Deactivate
          </button>
        )}
      </span>
    ),
});

/** The pill's own word for a payment, where the status alone would mislead. */
function statusLabel(p: PaymentSummary): string | undefined {
  if (p.status === "canceled" && p.source === "payment_link") return "Deactivated";
  // No token chosen yet: the payer may have left the checkout.
  if (!p.started && p.status === "awaiting_payment") return "Not started";
  if (!p.started && p.status === "expired") return "Expired, not started";
  return undefined;
}

const statusColumn: Column<PaymentSummary> = {
  key: "status",
  header: "Status",
  cell: (p) => (
    <PaymentStatusPill
      status={p.status}
      label={statusLabel(p)}
      tone={!p.started && p.status === "awaiting_payment" ? "neutral" : undefined}
    />
  ),
};

const fullColumns = (onDeactivate?: (p: PaymentSummary) => void): Column<PaymentSummary>[] => [
  amountColumn,
  statusColumn,
  paymentColumn,
  sourceColumn,
  createdColumn,
  actionsColumn(onDeactivate),
];

/** Columns that fit a narrow card: no source or actions, and no minimum table width. */
const compactColumns: Column<PaymentSummary>[] = [amountColumn, statusColumn, paymentColumn, createdColumn];

export type PaymentsTableVariant = "full" | "compact";

/**
 * The payments table: `full` on the Payments page, and `compact` on Home (recent payments),
 * where it shares a row with another card.
 */
export function PaymentsTable({
  rows,
  loading,
  dimmed,
  empty,
  skeletonRows,
  compact,
  variant = compact ? "compact" : "full",
  onDeactivate,
}: {
  rows: PaymentSummary[] | undefined;
  loading: boolean;
  dimmed?: boolean;
  empty: ReactNode;
  skeletonRows?: number;
  compact?: boolean;
  variant?: PaymentsTableVariant;
  /** Offer Deactivate on payment links that can still be paid. */
  onDeactivate?: (p: PaymentSummary) => void;
}) {
  return (
    <DataTable
      label="Payments"
      columns={variant === "compact" ? compactColumns : fullColumns(onDeactivate)}
      rows={rows}
      rowKey={(p) => p.id}
      rowHref={(p) => `/payments/${p.id}`}
      rowLabel={(p) => `${formatUsd(p.amount, p.currency)} payment ${p.payment_id}`}
      loading={loading}
      dimmed={dimmed}
      empty={empty}
      skeletonRows={skeletonRows}
      minWidth={variant === "compact" ? undefined : "48rem"}
    />
  );
}
